import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@app/store/utils/baseQueryWithReauth';
import { invalidateOnSuccess } from '@app/shared/utils/common';
import { updateBalance } from '@app/store/utils/cacheUpdate';
import { ApiResponse, extractData } from '@app/store/utils/common';
import {
  WithdrawalModel,
  WithdrawalHistoryResponse,
  CreateWithdrawalPayload,
  CancelWithdrawalPayload,
  WithdrawalTagType,
} from '@app/store/slices/withdrawal/withdrawalTypes';
import { BalanceActionType, PriceType } from '@app/shared/types/common';

export const withdrawalApi = createApi({
  reducerPath: 'withdrawalApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(WithdrawalTagType),
  endpoints: (builder) => ({
    getMyWithdrawal: builder.query<WithdrawalModel | null, void>({
      query: () => '/withdrawal',
      transformResponse: extractData,
      providesTags: [WithdrawalTagType.WITHDRAWAL],
    }),
    getWithdrawalHistory: builder.query<ApiResponse<WithdrawalHistoryResponse>, void>({
      query: () => '/withdrawal/history',
    }),
    createWithdrawal: builder.mutation<ApiResponse<WithdrawalModel>, CreateWithdrawalPayload>({
      query: (body) => ({
        url: '/withdrawal',
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([WithdrawalTagType.WITHDRAWAL]),
      onQueryStarted({ requestedAmount }, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          updateBalance(
            PriceType.TON,
            BalanceActionType.DECREASE,
            Number(requestedAmount),
            dispatch
          );
        });
      },
    }),
    cancelWithdrawal: builder.mutation<
      ApiResponse<null>,
      { body: CancelWithdrawalPayload; requestedAmount: number }
    >({
      query: ({ body }) => ({
        url: '/withdrawal',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([WithdrawalTagType.WITHDRAWAL]),
      onQueryStarted({ requestedAmount }, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          updateBalance(PriceType.TON, BalanceActionType.INCREASE, requestedAmount, dispatch);
        });
      },
    }),
  }),
});

export const { useGetMyWithdrawalQuery, useCreateWithdrawalMutation, useCancelWithdrawalMutation } =
  withdrawalApi;
